// Представление заказа для кабинета по макету «Профиль»: подпись и цвет статуса
// («Получите» — зелёный #32D47D, «Возврат» — красный #E1574F), шаг степпера
// «Оплачено → В обработке → Доставка → Получите», деление на актуальные и
// завершённые, короткий номер и даты.
import { orderStatusLabel as sharedStatusLabel } from "@osmix/contracts/order-status";
import type { Order } from "@/api";

type Status = Order["status"];

// Подпись статуса — общий словарь покупателя и продавца
// (@osmix/contracts/order-status, fd-order-01 от 31.10.2026): один статус
// называется одинаково в «Моих заказах» и в кабинете продавца. До этого
// витрина писала «Оплачен» / «На обработке» / «Получите», а кабинет про тот же
// заказ — «В работе» / «Отправлен» / «Доставлен».
export const orderStatusLabel = (status: Status) => sharedStatusLabel(status, "ru");

// Оплата прошла: всё, кроме «ждёт оплаты» и отказа. Не только `new`/`paid`:
// индивидуальный заказ из чата после оплаты сразу «Подтверждён» (fd-order-29).
const UNPAID: ReadonlySet<string> = new Set(["pending", "awaiting_payment", "declined", "cancelled"]);
export const isPaidOrderStatus = (status: string) => !UNPAID.has(status);

// Индивидуальный заказ (fd-request-13): позиция по смете или предложению из чата.
export const isIndividualOrder = (order: Pick<Order, "groups">) =>
	order.groups.some((g) => g.items.some((item) => Boolean(item.customOfferId)));

// Ориентировочная дата готовности от продавца (fd-order-08): показываем, пока
// заказ у продавца. «2026-11-05» → «05.11.2026».
const BEFORE_SHIPPING: ReadonlySet<Status> = new Set(["paid", "new", "processing", "confirmed", "in_work", "ready_to_ship"]);
export const readyDateLabel = (order: Pick<Order, "status" | "readyToShipAt">): string | null => {
	if (!order.readyToShipAt || !BEFORE_SHIPPING.has(order.status)) return null;
	const [y, m, d] = order.readyToShipAt.slice(0, 10).split("-");
	return y && m && d ? `${d}.${m}.${y}` : null;
};

export const orderStatusColor = (status: Status): string => {
	if (status === "delivered" || status === "awaiting_pickup") return "#32D47D";
	if (status === "return_requested" || status === "dispute_open" || status === "declined") return "#E1574F";
	if (status === "pending" || status === "awaiting_payment") return "#FA5C2E";
	if (status === "cancelled" || status === "archived") return "#1F1F1F7A";
	return "#1F1F1F";
};

const FINISHED: ReadonlySet<Status> = new Set(["completed", "cancelled", "declined", "archived"]);
export const isOrderFinished = (order: Order) => FINISHED.has(order.status);

// Сколько шагов степпера пройдено (-1 — ни одного, 3 — «Получите»).
export const orderStep = (status: Status): number => {
	switch (status) {
		case "paid":
		case "new":
		case "confirmed":
			return 0;
		case "processing":
		case "in_work":
		case "ready_to_ship":
			return 1;
		case "shipped":
		case "in_transit":
			return 2;
		case "awaiting_pickup":
		case "delivered":
		case "completed":
		case "return_requested":
		case "dispute_open":
			return 3;
		default:
			return -1;
	}
};

export const ORDER_STEPS = ["Оплачено", "В обработке", "Доставка", "Получите"] as const;

// Номер для подписи «Заказ №…»: короткий номер шлюза (123456, правка №13);
// пока его нет (старый образ) — id без служебного префикса ord_.
export const orderNumber = (id: string, number?: number | null) => (number ? String(number) : id.replace(/^ord_/, ""));

export const shortDate = (iso?: string) =>
	iso
		? new Date(iso).toLocaleDateString("ru-RU", {
				day: "2-digit",
				month: "2-digit",
				year: "2-digit",
			})
		: "";

export const shortTime = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }) : "");

export const dayMonth = (date: Date) => date.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });

// Момент текущего статуса: последнее событие истории, иначе оплата/создание.
export const statusAt = (order: Order) =>
	order.statusHistory?.filter((e) => e.newStatus === order.status).at(-1)?.createdAt ??
	(order.status === "completed" ? order.completedAt : undefined) ??
	order.paidAt ??
	order.createdAt;

export const pluralRu = (n: number, one: string, few: string, many: string) => {
	const m10 = n % 10;
	const m100 = n % 100;
	if (m10 === 1 && m100 !== 11) return one;
	if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
	return many;
};

// Ожидаемая дата доставки: дата оформления + верхняя граница срока тарифа
// («2-4» → 4 дня). Без срока — null (подпись без даты).
export const expectedDelivery = (order: Order): Date | null => {
	const days = order.groups
		.map((g) => g.shipment?.deliveryDays)
		.filter(Boolean)
		.map((d) => Math.max(...String(d).split(/\D+/).filter(Boolean).map(Number)))
		.filter((n) => Number.isFinite(n));
	if (days.length === 0) return null;
	const date = new Date(order.createdAt);
	date.setDate(date.getDate() + Math.max(...days));
	return date;
};
